import { useState, useRef, useMemo } from 'react';
import {
  ArrowIcon, EditIcon, DownloadIcon, RefreshIcon, CopyIcon, MoreIcon,
  HeartIcon, CompareIcon, FilmIcon,
} from './app-shell/icons';

/* ─── type normalisation ──────────────────────────────────────────────────── */

function normaliseType(rawType, url) {
  if (!rawType && !url) return 'image';
  const t = (rawType || '').toLowerCase();
  if (t === 'video' || t.startsWith('video/') || /\.(mp4|webm|mov|avi|mkv)(\?|$)/i.test(url || '')) return 'video';
  if (t === 'text') return 'text';
  return 'image';
}

/* ─── gallery job → canonical generation ────────────────────────────────── */

export function normalizeGalleryJob(job) {
  const isWorkflow = job.job_type === 'workflow';
  const rawResults = isWorkflow
    ? (Array.isArray(job.results?.results) ? job.results.results : Array.isArray(job.results) ? job.results : [])
    : (Array.isArray(job.results) ? job.results : []);

  const outputs = rawResults.map(r => {
    const url = r?.external_url || r?.url || (r?.content_type !== 'text' ? r?.content : null) || null;
    const content = r?.content_type === 'text' ? r?.content : null;
    return { url, content, type: normaliseType(r?.content_type, url) };
  }).filter(o => o.url || o.content);

  return {
    id: job.image_job_id || job.workflow_job_id,
    status: job.status || 'completed',
    model: {
      model_name: isWorkflow ? (job.workflow_definition_name || 'Workflow') : (job.model_name || 'Model'),
      model_id: job.model_id,
      workflow_definition_id: job.workflow_definition_id,
      isWorkflow,
    },
    outputs,
    images: outputs.filter(o => o.type === 'image').map(o => o.url).filter(Boolean),
    input_params: job.input_params || {},
    input_images: job.input_images || null,
    input_texts: job.input_texts || null,
    cost: job.cost_to_client,
    createdAt: job.created_at,
    is_liked: job.is_liked ?? false,
    error: job.error || null,
  };
}

/* ─── helpers ─────────────────────────────────────────────────────────────── */

export function getPrompt(generation) {
  const p = generation.input_params;
  if (!p) return generation.prompt || '';
  if (p.positive_prompt) return p.positive_prompt;
  if (p.prompt) return p.prompt;
  const k = Object.keys(p).find(k => k.toLowerCase().includes('prompt') && typeof p[k] === 'string' && p[k]);
  if (k) return p[k];
  const exclude = new Set(['image_url', 'image_urls', 'webhookURL', 'customTaskUUID']);
  const entry = Object.entries(p).find(([k, v]) =>
    typeof v === 'string' && v.length > 0 && v.length < 500 && !exclude.has(k) && !v.startsWith('http')
  );
  return entry ? entry[1] : (generation.prompt || '');
}

export function getOutputs(generation) {
  const { outputs = [], images = [] } = generation;
  if (outputs.length > 0) return outputs;
  return images.map(url => ({ url, type: 'image' }));
}

/** Every http(s) URL among a job's inputs — used for the input-vs-output compare.
 *  Zen keeps live inputs on `inputImages`; jobs from the API use `input_images`. */
export function getInputImages(generation) {
  const seen = [];
  const push = v => { if (typeof v === 'string' && /^https?:\/\//.test(v) && !seen.includes(v)) seen.push(v); };
  const scan = obj => Object.values(obj || {}).forEach(v => { if (Array.isArray(v)) v.forEach(push); else push(v); });
  scan(generation.input_params);
  scan(generation.input_images);
  scan(generation.inputImages);
  return seen;
}

export function getTimeAgo(date) {
  if (!date) return '';
  const s = Math.floor((Date.now() - new Date(date)) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export async function downloadMedia(url) {
  try {
    const blob = await fetch(url).then(r => r.blob());
    const ext = blob.type?.split('/')[1]?.split(';')[0]?.replace('jpeg', 'jpg') || url.match(/\.([a-z0-9]+)(?:[?#]|$)/i)?.[1] || 'png';
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `gen_${Date.now()}.${ext}`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 100);
  } catch {
    window.open(url, '_blank');
  }
}

/* ─── loading / error ─────────────────────────────────────────────────────── */

function LoadingCard({ model, prompt, index, total }) {
  return (
    <div style={{ background: 'rgba(255,255,255,.03)', borderRadius: 14, border: '1px solid rgba(255,255,255,.07)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,.06)' }}>
        <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg,#FF7E67,#FF4D8D)', display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0 }}>✦</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,.85)' }}>{model?.model_name || 'Generating…'}</div>
          {prompt && <div style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>{prompt}</div>}
        </div>
        <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, background: 'rgba(245,158,11,.12)', color: '#fbbf24', border: '1px solid rgba(245,158,11,.25)', flexShrink: 0 }}>
          {total > 1 ? `${index + 1}/${total}` : 'Generating…'}
        </span>
      </div>
      <div style={{ padding: '24px 16px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', aspectRatio: '1', maxHeight: 220, borderRadius: 12, overflow: 'hidden', position: 'relative', background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.06)' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(110deg,transparent 30%,rgba(255,77,141,.14) 50%,transparent 70%)', backgroundSize: '200% 100%', animation: 'gc-shimmer 1.6s linear infinite' }} />
          <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: 'rgba(255,255,255,.2)', fontSize: 11, fontFamily: 'monospace' }}>generating…</div>
        </div>
      </div>
      <style>{`@keyframes gc-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
    </div>
  );
}

function ErrorCard({ error, model, onRerun, onDismiss }) {
  const [expanded, setExpanded] = useState(false);
  const msg = error || 'Generation failed';
  const isLong = msg.length > 90;
  return (
    <div style={{ background: 'rgba(245,87,108,.05)', borderRadius: 14, border: '1px solid rgba(245,87,108,.15)', padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(245,87,108,.1)', display: 'grid', placeItems: 'center', color: '#f87171', flexShrink: 0 }}>✕</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          onClick={() => isLong && setExpanded(v => !v)}
          title={isLong ? (expanded ? 'Click to collapse' : 'Click to read the full error') : undefined}
          style={{
            fontSize: 13, color: '#f87171', fontWeight: 500, cursor: isLong ? 'pointer' : 'default',
            ...(expanded ? { wordBreak: 'break-word' } : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
          }}
        >{msg}</div>
        {model?.model_name && <div style={{ fontSize: 11, color: 'rgba(255,255,255,.35)', marginTop: 2 }}>{model.model_name}</div>}
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          {onRerun && (
            <button onClick={onRerun} style={{ padding: '5px 12px', background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.14)', borderRadius: 8, color: 'rgba(255,255,255,.8)', fontSize: 12, cursor: 'pointer' }}>
              Load params & retry
            </button>
          )}
          {onDismiss && (
            <button onClick={onDismiss} style={{ padding: '5px 12px', background: 'transparent', border: '1px solid rgba(255,255,255,.1)', borderRadius: 8, color: 'rgba(255,255,255,.45)', fontSize: 12, cursor: 'pointer' }}>
              Dismiss
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── always-visible icon action ──────────────────────────────────────────── */

function IconAction({ title, onClick, children, tone, size = 24 }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      title={title}
      aria-label={title}
      onClick={e => { e.stopPropagation(); onClick?.(e); }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: size, height: size, flexShrink: 0, borderRadius: 7, display: 'grid', placeItems: 'center',
        background: hov ? 'rgba(255,255,255,.22)' : 'rgba(0,0,0,.55)',
        border: '1px solid rgba(255,255,255,.18)',
        color: tone || (hov ? '#fff' : 'rgba(255,255,255,.82)'),
        cursor: 'pointer', padding: 0, backdropFilter: 'blur(8px)', transition: 'background .12s, color .12s',
      }}
    >
      {children}
    </button>
  );
}

/* ─── "use as input" target picker (multi image-param models) ─────────────── */

function UseAsInputAction({ imageParams, url, onUseAsInput, size }) {
  const [open, setOpen] = useState(false);
  const multi = (imageParams?.length || 0) > 1;

  if (!multi) {
    return (
      <IconAction size={size} title={imageParams?.[0]?.title ? `Use as ${imageParams[0].title}` : 'Use as input'} onClick={() => onUseAsInput(url, imageParams?.[0]?.paramName)}>
        <ArrowIcon s={12} />
      </IconAction>
    );
  }

  return (
    <div style={{ position: 'relative', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
      <IconAction size={size} title="Use as input…" onClick={() => setOpen(v => !v)}>
        <ArrowIcon s={12} />
      </IconAction>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 300 }} onClick={e => { e.stopPropagation(); setOpen(false); }} />
          <div style={{ position: 'absolute', bottom: '110%', right: 0, minWidth: 150, background: 'rgba(12,12,20,.98)', border: '1px solid rgba(255,255,255,.12)', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,.7)', zIndex: 301, overflow: 'hidden' }}>
            <div style={{ padding: '7px 11px', fontSize: 10, letterSpacing: '.04em', textTransform: 'uppercase', color: 'rgba(255,255,255,.36)' }}>Use as</div>
            {imageParams.map(p => (
              <button key={p.paramName}
                onClick={e => { e.stopPropagation(); onUseAsInput(url, p.paramName); setOpen(false); }}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 11px', background: 'transparent', border: 'none', color: 'rgba(255,255,255,.85)', fontSize: 12, cursor: 'pointer' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,.07)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >{p.title}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ─── video frame picker ──────────────────────────────────────────────────── */

function FramePickerAction({ url, onUseVideoFrame, size }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
      <IconAction size={size} title="Use a frame as input" onClick={() => setOpen(v => !v)}>
        <FilmIcon s={12} />
      </IconAction>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 300 }} onClick={e => { e.stopPropagation(); setOpen(false); }} />
          <div style={{ position: 'absolute', bottom: '110%', right: 0, minWidth: 150, background: 'rgba(12,12,20,.98)', border: '1px solid rgba(255,255,255,.12)', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,.7)', zIndex: 301, overflow: 'hidden' }}>
            {[['first', 'Use first frame'], ['last', 'Use last frame']].map(([k, l]) => (
              <button key={k}
                onClick={e => { e.stopPropagation(); onUseVideoFrame(url, k); setOpen(false); }}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 11px', background: 'transparent', border: 'none', color: 'rgba(255,255,255,.85)', fontSize: 12, cursor: 'pointer' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,.07)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >{l}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ─── text-only card ──────────────────────────────────────────────────────── */

function TextCard({ output, generation, onUseAsPrompt, onViewDetails, onRerun }) {
  const [hov, setHov] = useState(false);
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(output.content || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <div
      onClick={() => onViewDetails?.()}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: 'relative', borderRadius: 14, overflow: 'hidden', cursor: 'pointer',
        background: 'linear-gradient(135deg,rgba(56,189,248,.12),rgba(79,58,125,.2))',
        border: `1px solid ${hov ? 'rgba(255,255,255,.2)' : 'rgba(255,255,255,.08)'}`,
        transition: 'border-color .15s', minHeight: 140, padding: 16,
      }}
    >
      <p style={{ margin: '0 0 46px', fontSize: 13, color: 'rgba(255,255,255,.8)', lineHeight: 1.55,
        display: '-webkit-box', WebkitLineClamp: 6, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {output.content}
      </p>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '24px 12px 10px', background: 'linear-gradient(transparent,rgba(0,0,0,.75))' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {generation.model?.model_name || ''}
        </div>
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,.45)' }}>{getTimeAgo(generation.createdAt || generation.created_at)}</span>
          <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
            <IconAction title={copied ? 'Copied' : 'Copy text'} onClick={copy} tone={copied ? '#10B981' : undefined}><CopyIcon s={12} /></IconAction>
            {onUseAsPrompt && <IconAction title="Use as prompt" onClick={() => onUseAsPrompt(output.content)}><ArrowIcon s={12} /></IconAction>}
            {onRerun && <IconAction title="Reuse parameters" onClick={onRerun}><RefreshIcon s={12} /></IconAction>}
            {onViewDetails && <IconAction title="Details" onClick={onViewDetails}><MoreIcon s={12} /></IconAction>}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── media card (image / video) ──────────────────────────────────────────── */

function MediaCard({
  generation, primary, outputs, isActive, fit, badge,
  onViewDetails, onUseAsInput, onEditImage, onUseVideoFrame, onRerun, onToggleLike, onCompare,
  imageParams, liked, likePending,
}) {
  const [hov, setHov] = useState(false);
  const videoRef = useRef(null);
  const modelName = generation.model?.model_name || generation.model?.name || '';
  const timeAgo = getTimeAgo(generation.createdAt || generation.created_at);
  const totalCount = outputs.filter(o => o.url || o.content).length;
  const isVideo = primary.type === 'video';
  const square = fit === 'square';
  const hasInputs = getInputImages(generation).some(u => u !== primary.url);

  const handleMouseEnter = () => {
    setHov(true);
    if (isVideo && videoRef.current) videoRef.current.play().catch(() => {});
  };
  const handleMouseLeave = () => {
    setHov(false);
    if (isVideo && videoRef.current) { videoRef.current.pause(); videoRef.current.currentTime = 0; }
  };

  const mediaStyle = square
    ? { width: '100%', height: '100%', objectFit: 'cover', display: 'block' }
    : { width: '100%', display: 'block' };

  return (
    <div
      onClick={() => onViewDetails?.()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'relative', borderRadius: 14, overflow: 'hidden', cursor: 'pointer',
        background: 'rgba(255,255,255,.04)',
        ...(square ? { aspectRatio: '1 / 1' } : null),
        border: `2px solid ${isActive ? 'rgba(255,77,141,.55)' : hov ? 'rgba(255,255,255,.2)' : 'rgba(255,255,255,.08)'}`,
        boxShadow: isActive ? '0 20px 50px -20px rgba(255,77,141,.4)' : 'none',
        transition: 'border-color .15s, box-shadow .15s',
      }}
    >
      {isVideo
        ? <video ref={videoRef} src={primary.url} loop muted playsInline style={mediaStyle} />
        : <img src={primary.url} alt="" style={mediaStyle} loading="lazy" />
      }

      {/* Hover / active tint — painted over the media but UNDER the footer, so
          the action buttons never get dimmed by it. */}
      <div style={{ position: 'absolute', inset: 0, background: (hov || isActive) ? 'rgba(0,0,0,.22)' : 'rgba(0,0,0,0)', transition: 'background .15s', pointerEvents: 'none' }} />

      {/* Output position within a multi-output job, or the job's output count */}
      {(badge || totalCount > 1) && (
        <div style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,.82)', borderRadius: 6, padding: '3px 7px', display: 'flex', alignItems: 'center', gap: 4, pointerEvents: 'none' }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><rect x="7" y="7" width="10" height="10" rx="1"/></svg>
          <span style={{ color: '#fff', fontSize: 10, fontWeight: 600 }}>{badge || totalCount}</span>
        </div>
      )}

      {isVideo && (
        <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(0,0,0,.72)', borderRadius: 6, padding: '2px 6px', fontSize: 9, letterSpacing: '.06em', color: '#fff', fontWeight: 700, pointerEvents: 'none' }}>VIDEO</div>
      )}

      {/* Footer — model + time + ALWAYS-VISIBLE actions (no hover hunting) */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '34px 10px 10px', background: 'linear-gradient(transparent,rgba(0,0,0,.88))' }}>
        <div className="row" style={{ justifyContent: 'space-between', gap: 6 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{modelName}</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,.5)', marginTop: 1 }}>{timeAgo}</div>
          </div>
        </div>
        {/* nowrap + scroll: on a small grid tile a wrapping bar would climb up
            over the artwork. One row that scrolls sideways instead. */}
        <div className="no-scrollbar" style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'nowrap', overflowX: 'auto' }} onClick={e => e.stopPropagation()}>
          {isVideo
            ? onUseVideoFrame && <FramePickerAction url={primary.url} onUseVideoFrame={onUseVideoFrame} />
            : onUseAsInput && <UseAsInputAction imageParams={imageParams} url={primary.url} onUseAsInput={onUseAsInput} />
          }
          {!isVideo && onEditImage && (
            <IconAction title="Edit this image" onClick={() => onEditImage(primary.url)}><EditIcon s={12} /></IconAction>
          )}
          {onRerun && <IconAction title="Reuse these parameters" onClick={onRerun}><RefreshIcon s={12} /></IconAction>}
          <IconAction title={totalCount > 1 ? 'Download this output' : 'Download'} onClick={() => downloadMedia(primary.url)}><DownloadIcon s={12} /></IconAction>
          {/* Only when there IS an input to compare against — otherwise it was a
              button that could only ever toast "nothing to compare", and it cost
              a slot that pushed Details off the edge of a small tile. */}
          {onCompare && hasInputs && <IconAction title="Compare input vs output" onClick={() => onCompare(generation, primary.url)}><CompareIcon s={12} /></IconAction>}
          {onToggleLike && (
            <IconAction title={liked ? 'Unlike' : 'Like'} onClick={() => onToggleLike(generation)} tone={liked ? '#e63946' : undefined}>
              <span style={{ opacity: likePending ? 0.5 : 1, display: 'grid', placeItems: 'center' }}><HeartIcon s={12} filled={liked} /></span>
            </IconAction>
          )}
          {onViewDetails && <IconAction title="Details, parameters & API code" onClick={onViewDetails}><MoreIcon s={12} /></IconAction>}
        </div>
      </div>
    </div>
  );
}

/* ─── exported component ──────────────────────────────────────────────────── */

export default function GenerationCard({
  generation,
  isActive = false,
  /** Render this specific output instead of the job's primary one (flattened views). */
  output = null,
  /** "1/4"-style badge for flattened views. */
  badge = null,
  /** 'natural' keeps the media's own aspect; 'square' crops to an aligned grid tile. */
  fit = 'natural',
  onUseAsInput,
  onUseAsPrompt,
  onUseVideoFrame,
  onEditImage,
  onRerun,
  onViewDetails,
  onToggleLike,
  onCompare,
  onDismiss,
  liked = false,
  likePending = false,
  imageParams = [],
}) {
  const { model, status, error, failed_message } = generation;
  const isLoading = ['submitting', 'pending', 'processing'].includes(status);
  const isFailed  = status === 'failed';
  const prompt    = useMemo(() => getPrompt(generation), [generation]);
  const outputs   = useMemo(() => getOutputs(generation), [generation]);

  if (isLoading) return <LoadingCard model={model} prompt={prompt} index={generation.batchIndex || 0} total={generation.batchTotal || 1} />;
  if (isFailed)  return <ErrorCard error={error || failed_message} model={model} onRerun={onRerun} onDismiss={onDismiss} />;

  const chosen = output || null;
  const imageOutputs = outputs.filter(o => o.type !== 'video' && o.type !== 'text' && o.url);
  const videoOutputs = outputs.filter(o => o.type === 'video' && o.url);
  const textOutputs  = outputs.filter(o => o.type === 'text' && o.content);
  const primary      = chosen?.url ? chosen : (imageOutputs[0] || videoOutputs[0]);

  if (primary) {
    return (
      <MediaCard
        generation={generation}
        primary={primary}
        outputs={outputs}
        isActive={isActive}
        fit={fit}
        badge={badge}
        onViewDetails={onViewDetails}
        onUseAsInput={onUseAsInput}
        onEditImage={onEditImage}
        onUseVideoFrame={onUseVideoFrame}
        onRerun={onRerun}
        onToggleLike={onToggleLike}
        onCompare={onCompare}
        imageParams={imageParams}
        liked={liked}
        likePending={likePending}
      />
    );
  }

  const textOut = (chosen?.content ? chosen : textOutputs[0]);
  if (textOut) {
    return (
      <TextCard
        output={textOut}
        generation={generation}
        onUseAsPrompt={onUseAsPrompt}
        onViewDetails={onViewDetails}
        onRerun={onRerun}
      />
    );
  }

  return null;
}
